import i18n from 'i18next';
import Backend from 'i18next-locize-backend';
import LanguageDetector from 'i18next-browser-languagedetector';
import { initReactI18next } from 'react-i18next';
import { locizePlugin } from 'locize';
import {
  Tolgee,
  I18nextPlugin,
  DevTools,
  tolgeeProcessor,
  tolgeeApply,
  BackendFetch,
} from '@tolgee/i18next';
import { ENABLE_TOLGEE } from './utils/helper';
import { customBackend, tolgeeOptions } from './utils/tolgeeHelper';

export const locizeOptions = {
  projectId: process.env.REACT_APP_LOCIZE_PROJECT_ID,
  apiKey: process.env.REACT_APP_LOCIZE_API_KEY, // The API key should only be used in development, not in production. You should not =expose your apps API key to production!!!
  referenceLng: process.env.REACT_APP_LOCIZE_REFLNG,
  version: process.env.REACT_APP_LOCIZE_VERSION,
};

export const tolgeeInitOptions = {
  apiUrl: process.env.REACT_APP_TOLGEE_API_URL,
  apiKey: process.env.REACT_APP_TOLGEE_API_KEY,
  projectId: process.env.REACT_APP_TOLGEE_PROJECT_ID, //project Id needed for adding missing keys
  defaultLanguage: 'en',
  fallbackLanguage: 'en',
  backendFetch: {
    prefix:
    process.env.REACT_APP_TOLGEE_CDN_PREFIX
  },
};

const locizeI18Init = () => {
  i18n
  // load translation using http -> see /public/locales (i.e. https://github.com/i18next/react-i18next/tree/master/example/react/public/locales)
  // learn more: https://github.com/i18next/i18next-http-backend
  .use(Backend)
  // detect user language
  // learn more: https://github.com/i18next/i18next-browser-languageDetector
  .use(LanguageDetector)
  // pass the i18n instance to react-i18next.
  .use(initReactI18next)
  .use(locizePlugin)
  // init i18next
  // for all options read: https://www.i18next.com/overview/configuration-options
  .init({
    detection: {
      order: ['localStorage', 'navigator'],
      lookupLocalStorage: 'pds_i18nextLng',
    },
    caches: ['localStorage'],
    fallbackLng: 'en', // use en if detected lng is not available
    debug: false,
    saveMissing: process.env.REACT_APP_ENABLE_LOCIZE_SYNC === 'true', //creates new key if the key is missing in the namespace
    initImmediate: true,

    interpolation: {
      escapeValue: false, // react already safes from xss
      format: function (value, format, lng) {
        if (format === 'uppercase') return value.toUpperCase();
        if (format === 'lowercase') return value.toLowerCase();
        if (format === 'capitalize') return `${value.substr(0, 1).toUpperCase()}${value.substr(1)}`;
        return value;
      },
    },
    react: {
      useSuspense: false,
    },

    backend: locizeOptions,
  });
};

const tolgeeI18Init = () => {
  const withTolgee = (i18n, tolgee) => {
    i18n.use(customBackend(tolgee));
    i18n.use(tolgeeProcessor(tolgee));

    const originalInit = i18n.init;
    const newInit = (...params) => {
      tolgeeApply(tolgee, i18n);
      let options;
      let callback = undefined;

      if (typeof params[0] === 'object') {
        options = params[0];
        callback = params[1];
      } else {
        callback = params[0];
      }
      const newOptions = tolgeeOptions(tolgee, options);
      const result = originalInit.call(i18n, newOptions, callback);
      const language = i18n.language || options.lng;
      if (language) {
        tolgee.changeLanguage(language);
      }
      tolgee.run();
      return result;
    };
    i18n.init = newInit;
    return i18n;
  };

  let tolgeeInitialOptions = {
    defaultLanguage: tolgeeInitOptions.defaultLanguage,
    fallbackLanguage: tolgeeInitOptions.fallbackLanguage,
  };

  if (process.env.NODE_ENV !== 'production') {
    tolgeeInitialOptions = {
      ...tolgeeInitialOptions,
      apiKey: tolgeeInitOptions.apiKey,
      apiUrl: tolgeeInitOptions.apiUrl,
      projectId: tolgeeInitOptions.projectId,
    };
  }

  const tolgee = Tolgee()
    .use(BackendFetch(tolgeeInitOptions.backendFetch))
    // .use(DevTools())
    .use(I18nextPlugin())
    .init(tolgeeInitialOptions);

  withTolgee(i18n, tolgee)
    .use(customBackend(tolgee))
    .use(LanguageDetector)
    .use(initReactI18next)
    .init({
      detection: {
        order: ['localStorage', 'navigator'],
        lookupLocalStorage: 'pds_i18nextLng',
      },
      caches: ['localStorage'],
      fallbackLng: 'en', // use en if detected lng is not available
      debug: false,
      saveMissing: process.env.REACT_APP_TOLGEE_SYNC === 'true', //creates new key if the key is missing in the namespace
      initImmediate: true,
      interpolation: {
        escapeValue: false, // react already safes from xss
        format: function(value, format, lng) {
          if (format === 'uppercase') return value.toUpperCase();
          if (format === 'lowercase') return value.toLowerCase();
          if (format === 'capitalize')
            return `${value.substr(0, 1).toUpperCase()}${value.substr(1)}`;
          return value;
        },
      },
      react: {
        useSuspense: false,
      },
    });
};

ENABLE_TOLGEE ? tolgeeI18Init() : locizeI18Init();

i18n.on('languageChanged', lng => {
  document.documentElement.lang = lng;
});

export default i18n;
